import * as React from "react";
import { debounce } from "@/utils/debounce";
import {
  type ColumnDef,
  type ColumnFiltersState,
  type SortingState,
  type VisibilityState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Search, Sliders, CirclePlus, X, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { UserWithBuyerInfos } from "@/queryOptions/combined/userWithBuyerInfos/fetchPaginatedUserWithBuyerInfos";
import { UserWithSellerInfos } from "@/queryOptions/combined/userWithSellerInfos/fetchPaginatedUserWithSellerInfos";

// Define a union type for user with role info
type UserWithRoleInfo = UserWithBuyerInfos | UserWithSellerInfos;

type UsersDisplayTableProps = {
  users: UserWithRoleInfo[];
  columns: ColumnDef<any>[];
  onAddClient?: () => void;
  children?: React.ReactNode;
};

// Filter types
type FilterType = "all" | "stylist" | "distributor";

const userFilters = [
  { value: "all" as FilterType, label: "All Users" },
  { value: "stylist" as FilterType, label: "Stylist" },
  { value: "distributor" as FilterType, label: "Distributor" },
];

const UsersDisplayTable = ({ users, columns, onAddClient, children }: UsersDisplayTableProps) => {
  // States
  const [searchInput, setSearchInput] = React.useState("");
  const [activeFilters, setActiveFilters] = React.useState<FilterType[]>(["all"]);
  const [rowSelection, setRowSelection] = React.useState({});
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({});
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [pagination, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const filteredUsers = React.useMemo(() => {
    let filtered: UserWithRoleInfo[] = Array.isArray(users) ? users : [];

    if (searchInput.trim()) {
      const searchTerm = searchInput.toLowerCase().trim();
      filtered = filtered.filter((user: UserWithRoleInfo) => {
        const fullName = `${user.first_name || ""} ${user.last_name || ""}`.toLowerCase();
        const email = (user.email || "").toLowerCase();

        return fullName.includes(searchTerm) || email.includes(searchTerm);
      });
    }

    if (!activeFilters.includes("all")) {
      filtered = filtered.filter((user: UserWithRoleInfo) => {
        return activeFilters.some((filter) => {
          switch (filter) {
            case "stylist":
              return user.has_roles?.includes("buyer") && !user.has_roles?.includes("seller");
            case "distributor":
              return user.has_roles?.includes("seller");

            default:
              return false;
          }
        });
      });
    }

    return filtered;
  }, [users, searchInput, activeFilters]);

  
  const handleFilterToggle = (filterValue: FilterType) => {
    setActiveFilters((prev) => {
      if (filterValue === "all") {
        return ["all"];
      }

      const newFilters = prev.filter((f) => f !== "all");

      if (newFilters.includes(filterValue)) {
        const updated = newFilters.filter((f) => f !== filterValue);
        return updated.length === 0 ? ["all"] : updated;
      } else {
        return [...newFilters, filterValue];
      }
    });
  };

  
  const clearAllFilters = () => {
    setActiveFilters(["all"]);
    setSearchInput("");
  };

  
  const debouncedSearch = React.useMemo(
    () =>
      debounce((value: string) => {
        setSearchInput(value);
      }, 300),
    []
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    debouncedSearch(value);
  };

  
  const table = useReactTable({
    data: filteredUsers,
    columns,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
      columnFilters,
      pagination,
    },
    filterFns: {
      
      categoryFilter: () => true,
    },
    getRowId: (row) => row.id,
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
  });

  
  const activeFilterCount = activeFilters.filter((f) => f !== "all").length;

  return (
    <div className="py-4 mx-auto w-full ">
      
   

      
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name or email"
            className="pl-10 focus-visible:ring-2 focus-visible:ring-jmBluePrimary focus-visible:ring-offset-2 focus:border-none bg-white"
            onChange={handleSearchChange}
          />
        </div>

        <div className="flex gap-2 items-center">
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="flex w-full items-center gap-2 justify-between min-w-[120px] rounded-md hover:bg-transparent hover:text-black h-10"
              >
                <div className="flex items-center justify-start gap-2">
                  <Sliders className="h-4 w-4" />
                  Filter
                  {activeFilterCount > 0 && (
                    <Badge
                      variant="secondary"
                      className="ml-1 h-5 w-5 p-0 text-xs flex items-center justify-center"
                    >
                      {activeFilterCount}
                    </Badge>
                  )}
                </div>
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-white py-4 pr-10 pl-2">
              <DropdownMenuItem className="text-md hover:border-none hover:outline-none pl-2 font-medium pb-2 hover:bg-transparent cursor-pointer focus:bg-transparent">
                Filter by Status
              </DropdownMenuItem>

              {userFilters.map((filter) => (
                <DropdownMenuCheckboxItem
                  key={filter.value}
                  className="hover:border-none hover:outline-none hover:bg-gray-50 focus:bg-gray-50 cursor-pointer hover:text-black focus:text-black"
                  checked={activeFilters.includes(filter.value)}
                  onCheckedChange={() => handleFilterToggle(filter.value)}
                >
                  {filter.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          
          <Button
            onClick={onAddClient}
            className="bg-jmBluePrimary hover:bg-jmBlueSecondary text-white flex items-center gap-2 rounded-md"
          >
            <CirclePlus className="h-4 w-4" />
            Add Client
          </Button>
        </div>
      </div>


      {(activeFilterCount > 0 || searchInput) && (
        <div className="flex flex-wrap gap-2 mb-4 items-center">
          <span className="text-sm text-gray-600">Active filters:</span>

          
          {searchInput && (
            <Badge variant="outline" className="cursor-pointer hover:bg-gray-100">
              Search: "{searchInput}"
              <X
                className="ml-1 h-3 w-3"
                onClick={() => {
                  setSearchInput("");
                  
                  const input = document.querySelector(
                    'input[placeholder*="Search"]'
                  ) as HTMLInputElement;
                  if (input) input.value = "";
                }}
              />
            </Badge>
          )}

          
          {activeFilters
            .filter((filter) => filter !== "all")
            .map((filter) => (
              <Badge
                key={filter}
                variant="outline"
                className="cursor-pointer hover:bg-gray-100"
                onClick={() => handleFilterToggle(filter)}
              >
                {userFilters.find((f) => f.value === filter)?.label}
                <X className="ml-1 h-3 w-3" />
              </Badge>
            ))}

          
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs text-gray-500 hover:text-gray-700 hover:bg-transparent"
            onClick={clearAllFilters}
          >
            Clear all
          </Button>
        </div>
      )}

      
      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader className="sticky top-0 z-10 bg-muted">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id} colSpan={header.colSpan}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} data-state={row.getIsSelected() && "selected"}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  {searchInput || activeFilterCount > 0
                    ? "No Users found matching your criteria."
                    : "No Users found."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      
      <div className="flex items-center justify-between px-4 mt-4">
        <div className="flex w-full items-center justify-between gap-8 ">
          <div className="hidden items-center gap-2 lg:flex">
            <Label htmlFor="rows-per-page" className="text-sm font-medium">
              Rows per page
            </Label>
            <Select
              value={`${table.getState().pagination.pageSize}`}
              onValueChange={(value) => {
                table.setPageSize(Number(value));
              }}
            >
              <SelectTrigger className="w-20" id="rows-per-page">
                <SelectValue placeholder={table.getState().pagination.pageSize} />
              </SelectTrigger>
              <SelectContent side="top" className="bg-white">
                {[5, 10, 20, 30, 40, 50].map((pageSize) => (
                  <SelectItem key={pageSize} value={`${pageSize}`}>
                    {pageSize}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex w-full items-center gap-8 lg:w-fit">
            {children && <div className="ml-auto flex items-center gap-2 lg:ml-0">{children}</div>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsersDisplayTable;
